/**
 * RetroBoardWidget — first-class React renderer for the retro board widget.
 *
 * Architecture:
 *   - Reads `data.cards` from Loro and renders directly as React components.
 *     No dangerouslySetInnerHTML, no CSS-scale trick.
 *   - Inline card editing: click "+ New card" or the edit button → textarea
 *     renders inside the card. Draft content lives in React state so Loro
 *     re-syncs don't wipe in-progress text.
 *   - Card movement via HTML5 drag-and-drop:
 *       • Dragging within a column reorders cards.
 *       • Dragging to a different column moves the card there.
 *     A 2px drop-indicator line shows where the card will land.
 *   - "Summarize" toolbar button creates a full canvas agent session (right
 *     pane) with a pre-built prompt, following the CanvasToolbar pattern.
 */

import { useUserInfo } from "@/api/user";
import { cn } from "@/utils";
import { useReactFlow } from "@xyflow/react";
import { Pencil, ThumbsUp, Trash2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasApi } from "../../../context/CanvasContext";
import {
  RETRO_BOARD_OVERHEAD,
  RETRO_COLUMN_COLORS,
  RETRO_COLUMN_NAMES,
  RETRO_MIN_ZONE_H,
  calcZoneHeight,
  type RetroCard,
} from "./index";

// ── Design constants ──────────────────────────────────────────────────────────

/** Per-column contextual prompt shown in the empty zone — teaches the board. */
const EMPTY_PROMPTS = [
  "Who went above and beyond?\nRecognize great work.",
  "What's working well?\nKeep the momentum going.",
  "What's slowing us down?\nName it to fix it.",
  "What should we try next sprint?\nBring your experiments.",
] as const;

/** Subtle card background tint — sticky notes absorb color from their column. */
const CARD_TINTS = [
  { l: "rgba(255,237,153,.12)", d: "rgba(251,191,36,.08)" },
  { l: "rgba(167,243,208,.12)", d: "rgba(74,222,128,.08)" },
  { l: "rgba(254,182,182,.12)", d: "rgba(248,113,113,.08)" },
  { l: "rgba(199,210,254,.14)", d: "rgba(129,140,248,.08)" },
] as const;

function renderMd(raw: string): string {
  const safe = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return safe
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(
      /`(.*?)`/g,
      '<code style="background:rgba(0,0,0,.07);padding:1px 4px;border-radius:3px;font-family:monospace;font-size:.85em">$1</code>',
    )
    .replace(/\n/g, "<br>")
    .replace(
      /^[-*] (.+)$/gm,
      '<span style="display:block;padding-left:10px">• $1</span>',
    );
}

// ── Avatar helpers ────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#06b6d4",
  "#ef4444",
  "#3b82f6",
];
function avatarColor(name: string) {
  return AVATAR_PALETTE[
    [...name].reduce((s, c) => s + c.charCodeAt(0), 0) % AVATAR_PALETTE.length
  ];
}
function avatarInitials(name: string) {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0] ?? "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  );
}
function timeAgo(ts: number) {
  const d = Date.now() - ts;
  if (d < 60_000) return "just now";
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h`;
  return `${Math.floor(d / 86_400_000)}d`;
}

// ── Auto-growing textarea ─────────────────────────────────────────────────────

function AutoTextarea({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => {
      ref.current?.focus();
      const len = ref.current?.value.length ?? 0;
      ref.current?.setSelectionRange(len, len);
    }, 30);
    return () => clearTimeout(t);
  }, [autoFocus]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      rows={2}
      draggable={false}
      className={cn(
        "w-full resize-none overflow-hidden bg-transparent text-sm",
        "border-0 outline-none ring-0 leading-relaxed nodrag nowheel",
        "text-foreground placeholder:text-muted-foreground",
      )}
      style={{ minHeight: 36, pointerEvents: "all" }}
    />
  );
}

// ── Card component ────────────────────────────────────────────────────────────

interface RetroCardViewProps {
  card: RetroCard;
  isEditing: boolean;
  editDraft: string;
  canEdit: boolean;
  currentUserId: string;
  isDragging: boolean;
  isDropTarget: boolean;
  /** Column-tinted card background (light or dark variant). */
  cardBg: string;
  /** Column accent color — used for vote button and edit ring. */
  colAccent: string;
  onEditDraftChange: (v: string) => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onStartEdit: () => void;
  onDelete: () => void;
  onVote: () => void;
  // DnD
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
}

function RetroCardView({
  card,
  isEditing,
  editDraft,
  canEdit,
  currentUserId,
  isDragging,
  isDropTarget,
  cardBg,
  colAccent,
  onEditDraftChange,
  onSave,
  onCancelEdit,
  onStartEdit,
  onDelete,
  onVote,
  onDragStart,
  onDragEnd,
  onDragOver,
}: RetroCardViewProps) {
  const hasVoted = card.votes.includes(currentUserId);
  const voteCount = card.votes.length;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSave();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onCancelEdit();
      }
      e.stopPropagation();
    },
    [onSave, onCancelEdit],
  );

  return (
    <div
      data-retro-card={card.id}
      draggable={canEdit && !isEditing}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="nodrag"
      style={{ pointerEvents: "all" }}
    >
      {/* Drop indicator */}
      <div
        className={cn(
          "h-0.5 rounded-full mb-1 transition-all",
          isDropTarget ? "opacity-100" : "opacity-0",
        )}
        style={{ background: colAccent }}
      />

      <div
        data-card-inner="true"
        className={cn(
          "group relative rounded-xl border transition-all duration-150",
          "border-black/[0.07] dark:border-white/[0.15]",
          isEditing && "shadow-sm",
          isDragging && "opacity-0",
          canEdit &&
            !isEditing &&
            !isDragging &&
            "hover:shadow-md hover:border-black/[0.14] dark:hover:border-white/[0.28] cursor-grab active:cursor-grabbing",
        )}
        style={{ background: cardBg }}
      >
        {/* Editing ring using column accent */}
        {isEditing && (
          <div
            className="absolute inset-0 rounded-xl pointer-events-none ring-2 ring-canvas-selection ring-offset-0"
            style={{ boxShadow: `0 0 0 1.5px ${colAccent}` }}
          />
        )}

        {/* Meta row: avatar + name + timestamp + vote */}
        <div className="flex items-center gap-2 px-3 pt-2.5 pb-1">
          {card.authorAvatar ? (
            <img
              src={card.authorAvatar}
              alt=""
              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <span
              className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full flex-shrink-0 text-white"
              style={{
                background: avatarColor(card.authorName),
                fontSize: 7,
                fontWeight: 800,
              }}
            >
              {avatarInitials(card.authorName)}
            </span>
          )}
          <span className="text-[11px] font-semibold text-foreground/60 dark:text-foreground/65 flex-1 min-w-0 truncate leading-none">
            {card.authorName}
          </span>
          <span className="text-[10px] text-foreground/35 dark:text-foreground/35 flex-shrink-0 leading-none">
            {timeAgo(card.createdAt)}
          </span>

          {/* Vote — filled when voted, subtle when not */}
          <button
            draggable={false}
            onClick={(e) => {
              e.stopPropagation();
              onVote();
            }}
            className={cn(
              "flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border transition-all ml-0.5",
              hasVoted
                ? "text-white border-transparent"
                : "text-foreground/35 dark:text-foreground/40 border-black/10 dark:border-white/15 hover:border-black/25 dark:hover:border-white/30",
            )}
            style={
              hasVoted ? { background: colAccent, borderColor: colAccent } : {}
            }
          >
            <ThumbsUp className="w-2.5 h-2.5" />
            {voteCount > 0 && <span>{voteCount}</span>}
          </button>
        </div>

        {/* Content */}
        <div className="px-3 pb-2 min-h-[28px]">
          {isEditing ? (
            <AutoTextarea
              value={editDraft}
              onChange={onEditDraftChange}
              onBlur={onSave}
              onKeyDown={handleKeyDown}
              placeholder="What's on your mind?"
              autoFocus
            />
          ) : (
            <div
              className="text-[13px] leading-[1.55] text-foreground/82 dark:text-foreground/85 break-words cursor-text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html:
                  renderMd(card.content) ||
                  '<span style="opacity:.30;font-style:italic">Empty — double-click to edit</span>',
              }}
              onDoubleClick={() => canEdit && onStartEdit()}
            />
          )}
        </div>

        {/* Edit / delete actions — revealed on hover */}
        {canEdit && (
          <div
            className={cn(
              "flex items-center justify-end gap-1 px-2.5 pb-2 -mt-0.5",
              "opacity-0 group-hover:opacity-100 transition-opacity",
              isEditing && "opacity-100",
            )}
          >
            {isEditing ? (
              <>
                <button
                  draggable={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelEdit();
                  }}
                  className="text-[10px] font-medium text-foreground/45 hover:text-foreground/70 px-2 py-1 rounded-md transition-colors"
                  style={{ pointerEvents: "all" }}
                >
                  Cancel
                </button>
                <button
                  draggable={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSave();
                  }}
                  className="text-[10px] font-semibold px-2.5 py-1 rounded-md text-white transition-colors"
                  style={{ background: colAccent, pointerEvents: "all" }}
                >
                  Save
                </button>
              </>
            ) : (
              <>
                <button
                  draggable={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStartEdit();
                  }}
                  className="p-1 rounded text-foreground/35 hover:text-foreground/65 hover:bg-n-95/6 dark:hover:bg-n-95/8 transition-colors"
                  style={{ pointerEvents: "all" }}
                  title="Edit"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  draggable={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="p-1 rounded text-foreground/35 hover:text-lc-blocked hover:bg-lc-blocked/10 dark:hover:bg-lc-blocked/10 transition-colors"
                  style={{ pointerEvents: "all" }}
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface RetroBoardWidgetProps {
  id: string;
  data: { [key: string]: unknown };
  dragging?: boolean;
  width?: number;
  height?: number;
  positionAbsoluteX?: number;
  positionAbsoluteY?: number;
}

export function RetroBoardWidget({ id, data }: RetroBoardWidgetProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();

  const { data: userInfo } = useUserInfo();
  const currentUserId = userInfo?.user?.id ?? "anon";
  const authorName = userInfo?.user?.name ?? "Anonymous";
  const authorAvatar = userInfo?.user?.picture_url ?? undefined;

  const cards = Array.isArray(data.cards) ? (data.cards as RetroCard[]) : [];

  // ── Inline editing state ──────────────────────────────────────────────────
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const draftRef = useRef<string>("");
  const [draftValue, setDraftValue] = useState<string>("");

  const handleDraftChange = useCallback((v: string) => {
    draftRef.current = v;
    setDraftValue(v);
  }, []);

  const commitEdit = useCallback(
    (cardId: string) => {
      const content = draftRef.current.trim();
      const current = Array.isArray(data.cards)
        ? (data.cards as RetroCard[])
        : [];

      if (!content) {
        const existing = current.find((c) => c.id === cardId);
        if (!existing?.content) {
          const newCards = current.filter((c) => c.id !== cardId);
          const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
          canvas.updateNodeData(id, { cards: newCards });
          canvas.updateNodeDimensions(id, { height: newH });
          updateNode(id, { height: newH });
        }
        setEditingCardId(null);
        draftRef.current = "";
        setDraftValue("");
        return;
      }

      const idx = current.findIndex((c) => c.id === cardId);
      if (idx === -1) {
        setEditingCardId(null);
        return;
      }

      const newCards = [...current];
      newCards[idx] = { ...newCards[idx], content };
      const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
      canvas.updateNodeData(id, { cards: newCards });
      canvas.updateNodeDimensions(id, { height: newH });
      updateNode(id, { height: newH });
      setEditingCardId(null);
      draftRef.current = "";
      setDraftValue("");
    },
    [data.cards, id, canvas, updateNode],
  );

  const cancelEdit = useCallback(
    (cardId: string) => {
      const current = Array.isArray(data.cards)
        ? (data.cards as RetroCard[])
        : [];
      const card = current.find((c) => c.id === cardId);
      if (card && !card.content && !draftRef.current.trim()) {
        const newCards = current.filter((c) => c.id !== cardId);
        const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
        canvas.updateNodeData(id, { cards: newCards });
        canvas.updateNodeDimensions(id, { height: newH });
        updateNode(id, { height: newH });
      }
      setEditingCardId(null);
      draftRef.current = "";
      setDraftValue("");
    },
    [data.cards, id, canvas, updateNode],
  );

  // ── Card add / delete / vote ──────────────────────────────────────────────

  const addCard = useCallback(
    (columnIndex: 0 | 1 | 2 | 3) => {
      if (!canEdit) return;
      const current = Array.isArray(data.cards)
        ? (data.cards as RetroCard[])
        : [];
      const newCard: RetroCard = {
        id: uuidv4(),
        columnIndex,
        content: "",
        authorName,
        authorAvatar,
        votes: [],
        createdAt: Date.now(),
      };
      const newCards = [...current, newCard];
      const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
      canvas.updateNodeData(id, { cards: newCards });
      canvas.updateNodeDimensions(id, { height: newH });
      updateNode(id, { height: newH });
      draftRef.current = "";
      setDraftValue("");
      setEditingCardId(newCard.id);
    },
    [canEdit, data.cards, authorName, authorAvatar, id, canvas, updateNode],
  );

  const deleteCard = useCallback(
    (cardId: string) => {
      if (!canEdit) return;
      const newCards = (
        Array.isArray(data.cards) ? (data.cards as RetroCard[]) : []
      ).filter((c) => c.id !== cardId);
      const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
      canvas.updateNodeData(id, { cards: newCards });
      canvas.updateNodeDimensions(id, { height: newH });
      updateNode(id, { height: newH });
      if (editingCardId === cardId) {
        setEditingCardId(null);
        draftRef.current = "";
        setDraftValue("");
      }
    },
    [canEdit, data.cards, id, canvas, updateNode, editingCardId],
  );

  const voteCard = useCallback(
    (cardId: string) => {
      const current = Array.isArray(data.cards)
        ? (data.cards as RetroCard[])
        : [];
      const idx = current.findIndex((c) => c.id === cardId);
      if (idx === -1) return;
      const card = current[idx];
      const alreadyVoted = card.votes.includes(currentUserId);
      const newCards = [...current];
      newCards[idx] = {
        ...card,
        votes: alreadyVoted
          ? card.votes.filter((v) => v !== currentUserId)
          : [...card.votes, currentUserId],
      };
      canvas.updateNodeData(id, { cards: newCards });
    },
    [data.cards, currentUserId, id, canvas],
  );

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  //
  // Architecture: the entire COLUMN div is the drop zone, not just the zone/card.
  // Cards only handle onDragStart/End/Over (to set the visual indicator position).
  // Cards do NOT stop propagation on dragover — events bubble to the column div.
  // The column div handles all onDrop events.
  //
  // dropTarget tracks where the card will land:
  //   { columnIndex, beforeCardId: string } → insert before that card
  //   { columnIndex, beforeCardId: null }   → append at end of column

  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    columnIndex: 0 | 1 | 2 | 3;
    beforeCardId: string | null;
  } | null>(null);

  // Ref so the column onDrop closure always reads current dropTarget without
  // needing it in the dependency array (avoids stale closure issues).
  const dropTargetRef = useRef(dropTarget);
  dropTargetRef.current = dropTarget;

  const moveCard = useCallback(
    (
      cardId: string,
      targetColumn: 0 | 1 | 2 | 3,
      beforeCardId: string | null,
    ) => {
      const current = Array.isArray(data.cards)
        ? (data.cards as RetroCard[])
        : [];
      const cardIdx = current.findIndex((c) => c.id === cardId);
      if (cardIdx === -1) return;

      const movedCard = { ...current[cardIdx], columnIndex: targetColumn };
      const withoutCard = current.filter((c) => c.id !== cardId);

      let newCards: RetroCard[];
      if (beforeCardId === null) {
        const lastIdx = withoutCard.reduce<number>(
          (acc, c, i) => (c.columnIndex === targetColumn ? i : acc),
          -1,
        );
        if (lastIdx === -1) {
          newCards = [...withoutCard, movedCard];
        } else {
          newCards = [
            ...withoutCard.slice(0, lastIdx + 1),
            movedCard,
            ...withoutCard.slice(lastIdx + 1),
          ];
        }
      } else {
        const insertIdx = withoutCard.findIndex((c) => c.id === beforeCardId);
        if (insertIdx === -1) {
          newCards = [...withoutCard, movedCard];
        } else {
          newCards = [
            ...withoutCard.slice(0, insertIdx),
            movedCard,
            ...withoutCard.slice(insertIdx),
          ];
        }
      }

      const newH = calcZoneHeight(newCards) + RETRO_BOARD_OVERHEAD;
      canvas.updateNodeData(id, { cards: newCards });
      canvas.updateNodeDimensions(id, { height: newH });
      updateNode(id, { height: newH });
    },
    [data.cards, id, canvas, updateNode],
  );

  // Card handlers — only drag start/end/over.
  // onDragOver updates the visual indicator but does NOT stop propagation,
  // so the event bubbles to the column where preventDefault is called.
  const makeCardDragHandlers = useCallback(
    (card: RetroCard, columnIndex: 0 | 1 | 2 | 3) => ({
      onDragStart: (e: React.DragEvent) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", card.id);

        // Capture the inner card element (the visible card surface) as the
        // drag ghost so the preview looks exactly like the real card.
        const outer = e.currentTarget as HTMLElement;
        const inner =
          outer.querySelector<HTMLElement>('[data-card-inner="true"]') ?? outer;
        const rect = inner.getBoundingClientRect();
        e.dataTransfer.setDragImage(
          inner,
          e.clientX - rect.left,
          e.clientY - rect.top,
        );

        // Inject a <style> tag to force grabbing cursor everywhere.
        // document.body.style.cursor is ignored by the browser during native
        // HTML5 DnD — only !important on * reliably overrides the OS cursor.
        let styleTag = document.getElementById(
          "__retro-drag-cursor",
        ) as HTMLStyleElement | null;
        if (!styleTag) {
          styleTag = document.createElement("style");
          styleTag.id = "__retro-drag-cursor";
          document.head.appendChild(styleTag);
        }
        styleTag.textContent = "* { cursor: grabbing !important; }";

        // Hide the original AFTER the ghost snapshot is taken (next frame).
        // Setting it synchronously would capture a transparent element.
        requestAnimationFrame(() => setDraggingCardId(card.id));
      },
      onDragEnd: (e: React.DragEvent) => {
        e.stopPropagation();
        document.getElementById("__retro-drag-cursor")?.remove();
        setDraggingCardId(null);
        setDropTarget(null);
      },
      // Update indicator position; bubble to column for preventDefault + onDrop.
      onDragOver: (_e: React.DragEvent) => {
        setDropTarget({ columnIndex, beforeCardId: card.id });
      },
    }),
    [],
  );

  // Column handlers — cover the entire column (header + zone + cards).
  // onDragOver: calls preventDefault (enabling drop) and sets target to "end of
  //   column" only when not hovering over a card (detected via data-retro-card).
  // onDrop: uses dropTargetRef to read the current position and perform the move.
  const makeColumnDragHandlers = useCallback(
    (columnIndex: 0 | 1 | 2 | 3) => ({
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        // If hovering over the column background (not a card), target = end of column
        const isOverCard = !!(e.target as HTMLElement).closest(
          "[data-retro-card]",
        );
        if (!isOverCard) {
          setDropTarget({ columnIndex, beforeCardId: null });
        }
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const draggedId = e.dataTransfer.getData("text/plain");
        if (draggedId) {
          const dt = dropTargetRef.current;
          // Use the stored drop position if it belongs to this column, otherwise append
          const beforeCardId =
            dt?.columnIndex === columnIndex ? dt.beforeCardId : null;
          moveCard(draggedId, columnIndex, beforeCardId);
        }
        setDraggingCardId(null);
        setDropTarget(null);
      },
      onDragLeave: (e: React.DragEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const { clientX, clientY } = e;
        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          setDropTarget(null);
        }
      },
    }),
    [moveCard],
  );

  const zoneH = Math.max(RETRO_MIN_ZONE_H, calcZoneHeight(cards));

  // ── Render ────────────────────────────────────────────────────────────────
  // NodeResizer, toolbar, and AI summary action are all managed by
  // ReactWidgetShell using the preset's resizable + toolbarSlot fields.

  return (
    <>
      <div
        className={cn(
          "w-full h-full rounded-[20px] overflow-hidden font-sans",
          "bg-n-00/[0.96] dark:bg-[rgba(16,14,24,0.98)]",
          "border border-black/[0.09] dark:border-white/[0.22]",
          "shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-[0_6px_32px_rgba(0,0,0,0.65)]",
        )}
        style={{ minWidth: 800, pointerEvents: "none" }}
      >
        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-black/[0.07] dark:border-white/[0.12]">
          <span className="text-[11px] font-bold tracking-[.10em] uppercase text-foreground/50 dark:text-foreground/75">
            Sprint Retrospective
          </span>
          {cards.length > 0 && (
            <span className="ml-auto text-[10px] text-foreground/35 dark:text-foreground/40">
              {cards.length} card{cards.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* ── Column grid ───────────────────────────────────────────────────── */}
        <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {([0, 1, 2, 3] as const).map((ci) => {
            const col = RETRO_COLUMN_COLORS[ci];
            const colCards = cards.filter((c) => c.columnIndex === ci);
            const isEndDropTarget =
              dropTarget?.columnIndex === ci &&
              dropTarget.beforeCardId === null;
            const colDragHandlers = makeColumnDragHandlers(ci);
            const isDark =
              typeof document !== "undefined" &&
              document.documentElement.classList.contains("dark");
            const colName = isDark ? col.nameDark : col.name;
            const colBg = isDark ? col.bgDark : col.bg;
            const colBorder = isDark ? col.borderDark : col.border;
            const cardBg = isDark ? CARD_TINTS[ci].d : CARD_TINTS[ci].l;

            return (
              <div
                key={ci}
                className={cn(
                  "p-4 nodrag",
                  ci > 0 &&
                    "border-l border-black/[0.05] dark:border-white/[0.10]",
                )}
                style={{ pointerEvents: "all" }}
                {...colDragHandlers}
              >
                {/* ── Column header — thick accent bar instead of pip dot ──── */}
                <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-black/[0.07] dark:border-white/[0.10]">
                  <div
                    className="w-[3px] h-[18px] rounded-full flex-shrink-0"
                    style={{ background: col.pip }}
                  />
                  <span
                    className="text-[13px] font-bold tracking-tight flex-1"
                    style={{ color: colName }}
                  >
                    {RETRO_COLUMN_NAMES[ci]}
                  </span>
                  {colCards.length > 0 && (
                    <span
                      className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 text-foreground/45 dark:text-foreground/50"
                      style={{
                        background: isDark ? `${col.pip}22` : `${col.pip}18`,
                      }}
                    >
                      {colCards.length}
                    </span>
                  )}
                </div>

                {/* ── Zone ───────────────────────────────────────────────── */}
                <div
                  className="nodrag rounded-2xl border-[1.5px] border-dashed flex flex-col p-2.5 overflow-hidden"
                  style={{
                    background: colBg,
                    borderColor: colBorder,
                    minHeight: zoneH,
                    transition: "min-height 0.25s ease",
                  }}
                >
                  {/* Cards */}
                  <div className="flex flex-col gap-2 flex-1">
                    {colCards.map((card) => {
                      const dndHandlers = makeCardDragHandlers(card, ci);
                      return (
                        <RetroCardView
                          key={card.id}
                          card={card}
                          isEditing={editingCardId === card.id}
                          editDraft={
                            editingCardId === card.id ? draftValue : ""
                          }
                          canEdit={canEdit}
                          currentUserId={currentUserId}
                          isDragging={draggingCardId === card.id}
                          isDropTarget={dropTarget?.beforeCardId === card.id}
                          cardBg={cardBg}
                          colAccent={col.pip}
                          onEditDraftChange={handleDraftChange}
                          onSave={() =>
                            editingCardId === card.id && commitEdit(card.id)
                          }
                          onCancelEdit={() => cancelEdit(card.id)}
                          onStartEdit={() => {
                            draftRef.current = card.content;
                            setDraftValue(card.content);
                            setEditingCardId(card.id);
                          }}
                          onDelete={() => deleteCard(card.id)}
                          onVote={() => voteCard(card.id)}
                          onDragStart={dndHandlers.onDragStart}
                          onDragEnd={dndHandlers.onDragEnd}
                          onDragOver={dndHandlers.onDragOver}
                        />
                      );
                    })}

                    {/* Drop-at-end indicator */}
                    <div
                      className={cn(
                        "h-0.5 rounded-full transition-all mt-1",
                        isEndDropTarget ? "opacity-100" : "opacity-0",
                      )}
                      style={{ background: col.pip }}
                    />
                  </div>

                  {/* Empty state — contextual prompt, nudged above true center */}
                  {colCards.length === 0 && !draggingCardId && (
                    <div
                      className="flex-1 flex items-center justify-center px-4 pointer-events-none"
                      style={{ paddingBottom: "30%" }}
                    >
                      <p
                        className="text-center text-[11px] leading-loose whitespace-pre-line"
                        style={{
                          color: isDark ? `${col.nameDark}99` : `${col.name}b0`,
                        }}
                      >
                        {EMPTY_PROMPTS[ci]}
                      </p>
                    </div>
                  )}

                  {/* + New card — accent-colored, column-specific */}
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addCard(ci);
                      }}
                      className="nodrag flex items-center justify-center gap-2 py-2.5 rounded-xl mt-2 transition-all text-[11px] font-semibold"
                      style={{
                        borderWidth: "1.5px",
                        borderStyle: "dashed",
                        borderColor: isDark ? `${col.pip}55` : `${col.pip}66`,
                        color: isDark ? `${col.pip}cc` : colName,
                        background: "transparent",
                        pointerEvents: "all",
                      }}
                      onMouseEnter={(e) => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = isDark
                          ? `${col.pip}12`
                          : `${col.pip}14`;
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.borderColor = isDark
                          ? `${col.pip}88`
                          : `${col.pip}99`;
                      }}
                      onMouseLeave={(e) => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.background = "transparent";
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.borderColor = isDark
                          ? `${col.pip}55`
                          : `${col.pip}66`;
                      }}
                    >
                      <span className="text-base leading-none font-light">
                        +
                      </span>
                      New card
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Footer strip — visual breathing room below columns ──────────── */}
        <div className="h-3 bg-n-95/[0.02] dark:bg-n-95/[0.03] border-t border-black/[0.04] dark:border-white/[0.07]" />
      </div>
    </>
  );
}
